import { useCallback } from "react";
import { useFirebase } from "../context/FirebaseContext";
import { invalidateCachedValue } from "../utility/localDataCache";
import {
  markActivityLogStale,
  markNotificationsLogStale
} from "../utility/activityFeedFreshness";
import { getUserProfileCacheKey } from "../utility/profileCache";
import { isDemoUserId } from "../utility/demoSession";
import {
  hasDemoActivity,
  logDemoActivity,
  removeDemoActivity
} from "../utility/demoSandbox";
import { getActivityDocumentId } from "../utility/activityIdentity";
import { sanitizeActivityMetadata } from "../utility/firestoreActivity";
import type firebase from "firebase/compat";
import type { ActivityMetadata } from "../types/activity";
import type { EntityId } from "../types/media";
import type { ExperienceEventInput, ProgressExperienceItem } from "../types/experience";
import type { CelebrationEvent } from "../context/celebrationTypes";

interface ActivityLogMetadata extends Omit<ActivityMetadata, "challengeId"> {
  dedupeKey?: string;
  actorName?: string;
  rating?: number | null;
  oldRating?: number | null;
  isUpdate?: boolean;
  experienceGained?: number;
  newLevel?: number;
  reviewTitle?: string;
  challengeId?: EntityId;
  challengeTitle?: string;
  challengeDifficulty?: string;
  weeklyCardRarity?: string;
  weeklyCardTitle?: string;
  dungeonTitle?: string;
  dungeonRarity?: string;
  stageId?: string | null;
  stageTitle?: string;
  weekId?: string;
  weekKey?: string;
  streakWeekNumber?: number;
  sourceType?: string;
  experience?: number;
  revokedMovieIds?: string[];
}
interface ExperienceChangeNotification {
  userId: string;
  type: string;
  experience: number;
  metadata: Record<string, unknown>;
}
interface ActivityAdjustmentInput extends ExperienceEventInput {
  weekKey?: string;
  streakWeekNumber?: number;
}
interface LevelUpActivityInput { filmTitle?: string; newLevel?: number; experienceGained?: number; oldLevel?: number; }


const invalidateCurrentMyResultsActivityCaches = (userId: string) => {
  const now = new Date();
  const year = now.getFullYear();
  const monthKey = `${year}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  invalidateCachedValue(`my-results-activities-v2-${userId}-month-${monthKey}`);
  invalidateCachedValue(`my-results-activities-v2-${userId}-year-${year}`);
};

export const useActivityLogger = () => {
  const { auth, firestore, firebase } = useFirebase();

  const notify = useCallback((event: CelebrationEvent) => {
    if (typeof window === "undefined" || !event) return;
    window.dispatchEvent(new CustomEvent("movie-celebration", { detail: event }));
  }, []);

  const notifyExperienceChanged = useCallback((event: ExperienceChangeNotification) => {
    if (typeof window === "undefined" || !event) return;
    window.dispatchEvent(new CustomEvent("movie-profile-experience-changed", { detail: event }));
  }, []);

  const logActivity = useCallback(async (type: string, title: string | undefined, description: string = "", experience: number = 0, metadata: ActivityLogMetadata = {}) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const metadataObject = metadata && typeof metadata === "object" ? metadata : {};
    const { dedupeKey, ...rawStoredMetadata } = metadataObject;
    const storedMetadata = sanitizeActivityMetadata(rawStoredMetadata);
    const activity = {
      actorId: uid,
      type,
      title: String(title || ""),
      description: String(description || ""),
      experience: Number(experience) || 0,
      metadata: {
        actorName: auth.currentUser?.displayName || auth.currentUser?.email?.split("@")[0] || "Пользователь",
        ...storedMetadata
      },
      timestamp: firebase.firestore.FieldValue.serverTimestamp()
    };
    if (isDemoUserId(uid)) {
      const created = logDemoActivity(uid, {
        ...activity,
        timestamp: new Date().toISOString()
      }, dedupeKey);
      if (created && activity.experience !== 0) {
        notifyExperienceChanged({
          userId: uid,
          type,
          experience: activity.experience,
          metadata: storedMetadata
        });
      }
      return created;
    }
    const writeActivity = async (collectionReference: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>) => {
      if (!dedupeKey) {
        await collectionReference.add(activity);
        return true;
      }
      const documentReference = collectionReference.doc(getActivityDocumentId(dedupeKey));
      return firestore.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(documentReference);
        if (snapshot.exists) return false;
        transaction.set(documentReference, activity);
        return true;
      });
    };
    try {
      const primary = firestore.collection("userActivities").doc(uid).collection("activities");
      const created = await writeActivity(primary);
      if (created) {
        invalidateCurrentMyResultsActivityCaches(uid);
        if (activity.experience !== 0) {
          invalidateCachedValue(getUserProfileCacheKey(uid));
          notifyExperienceChanged({
            userId: uid,
            type,
            experience: activity.experience,
            metadata: storedMetadata
          });
        }
        markActivityLogStale(uid);
        markNotificationsLogStale(uid, "viewed");
      }
      return created;
    } catch (legacyError) {
      try {
        const fallback = firestore.collection("users").doc(uid).collection("activities");
        const created = await writeActivity(fallback);
        if (created) {
          invalidateCurrentMyResultsActivityCaches(uid);
          if (activity.experience !== 0) {
            invalidateCachedValue(getUserProfileCacheKey(uid));
            notifyExperienceChanged({
              userId: uid,
              type,
              experience: activity.experience,
              metadata: storedMetadata
            });
          }
          markActivityLogStale(uid);
          markNotificationsLogStale(uid, "viewed");
        }
        return created;
      } catch (fallbackError) {
        console.error("Ошибка записи события в дневник:", legacyError, fallbackError);
        return false;
      }
    }
  }, [auth, firebase, firestore, notifyExperienceChanged]);

  const hasActivity = useCallback(async (dedupeKey: string | undefined) => {
    const uid = auth.currentUser?.uid;
    if (!uid || !dedupeKey) return false;
    if (isDemoUserId(uid)) return hasDemoActivity(uid, dedupeKey);
    const id = getActivityDocumentId(dedupeKey);
    let primaryError: unknown = null;

    try {
      const primary = await firestore
        .collection("userActivities")
        .doc(uid)
        .collection("activities")
        .doc(id)
        .get();
      if (primary.exists) return true;
    } catch (error) {
      primaryError = error;
    }

    try {
      const fallback = await firestore
        .collection("users")
        .doc(uid)
        .collection("activities")
        .doc(id)
        .get();
      return fallback.exists;
    } catch (fallbackError) {
      if (primaryError) {
        console.warn("Не удалось проверить событие в дневнике:", primaryError, fallbackError);
      }
    }

    return false;
  }, [auth, firestore]);

  const removeActivity = useCallback(async (dedupeKey: string | undefined) => {
    const uid = auth.currentUser?.uid;
    if (!uid || !dedupeKey) return false;
    if (isDemoUserId(uid)) return removeDemoActivity(uid, dedupeKey);
    const id = getActivityDocumentId(dedupeKey);
    const references = [
      firestore.collection("userActivities").doc(uid).collection("activities").doc(id),
      firestore.collection("users").doc(uid).collection("activities").doc(id)
    ];
    const results = await Promise.allSettled(
      references.map((reference) => reference.delete())
    );
    const removed = results.some((result) => result.status === "fulfilled");
    if (!removed) return false;

    invalidateCurrentMyResultsActivityCaches(uid);
    markActivityLogStale(uid);
    markNotificationsLogStale(uid, "viewed");
    return true;
  }, [auth, firestore]);

  const logRating = useCallback((data: ActivityLogMetadata | string, rating?: number | null, experience: number = 10) => {
    const payload = typeof data === "object" ? data : { filmTitle: data, rating };
    const isUpdate = payload.isUpdate || payload.oldRating != null;
    return logActivity(
      "rating",
      isUpdate ? `Изменена оценка «${payload.filmTitle || "Фильм"}»` : `Просмотрен фильм «${payload.filmTitle || "Фильм"}»`,
      payload.rating == null ? "Просмотрен без оценки" : `Оценка: ${payload.rating}/10`,
      isUpdate ? 0 : experience,
      payload
    );
  }, [logActivity]);

  const logAchievement = useCallback(async (title: string | undefined, experience: number = 0) => {
    const ok = await logActivity(
      "achievement", `Получено достижение «${title}»`, "Новое достижение разблокировано", experience,
      { achievementTitle: title, dedupeKey: `achievement:${title}` }
    );
    if (ok) notify({
      scope: "own",
      type: "achievement",
      category: "progress_achievement",
      mode: "modal",
      title: `Новое достижение: ${title}`,
      description: experience ? `+${experience} опыта` : "Достижение разблокировано",
      experience,
      metadata: { achievementTitle: title },
      key: `achievement:${title}`
    });
    return ok;
  }, [logActivity, notify]);

  const logExperienceAdjustment = useCallback(async ({
    title,
    description = "",
    experience = 0,
    dedupeKey,
    metadata = {}
  }: ActivityAdjustmentInput = {}) => {
    const normalizedExperience = Number(experience) || 0;
    if (!normalizedExperience || !dedupeKey) return false;
    const ok = await logActivity(
      "experience",
      title || `${normalizedExperience > 0 ? "+" : ""}${normalizedExperience} опыта`,
      description,
      normalizedExperience,
      { ...metadata, dedupeKey }
    );
    if (ok) {
      notify({
        scope: "own",
        type: "experience",
        category: "progress_experience",
        mode: "toast",
        title: title || `${normalizedExperience > 0 ? "+" : ""}${normalizedExperience} опыта`,
        description,
        experience: normalizedExperience,
        metadata,
        key: dedupeKey
      });
    }
    return ok;
  }, [logActivity, notify]);

  const logStreakProgress = useCallback(async ({
    title,
    description = "",
    experience = 0,
    dedupeKey,
    weekKey,
    streakWeekNumber,
    metadata = {}
  }: ActivityAdjustmentInput = {}) => {
    const normalizedExperience = Number(experience) || 0;
    if (!normalizedExperience || !dedupeKey) return false;
    const normalizedMetadata = {
      ...metadata,
      sourceType: "weekly_streak",
      weekKey,
      streakWeekNumber: Math.max(0, Math.trunc(Number(streakWeekNumber) || 0)),
      experience: normalizedExperience
    };
    const eventTitle = title || "Недельная серия";
    const ok = await logActivity(
      "streak",
      eventTitle,
      description,
      normalizedExperience,
      { ...normalizedMetadata, dedupeKey }
    );
    if (ok) {
      notify({
        scope: "own",
        type: "streak",
        category: "progress_streak",
        mode: "toast",
        title: eventTitle,
        description,
        experience: normalizedExperience,
        metadata: normalizedMetadata,
        key: dedupeKey
      });
    }
    return ok;
  }, [logActivity, notify]);

  const logLevelUp = useCallback(async (data: LevelUpActivityInput | number, experience: number = 0) => {
    const payload = typeof data === "object" ? data : { newLevel: data, experienceGained: experience };
    const ok = await logActivity(
      "level_up", `Достигнут ${payload.newLevel} уровень`, "Новая глава киноманской истории", 0,
      { ...payload, dedupeKey: `level:${payload.newLevel}` }
    );
    if (ok) notify({
      scope: "own",
      type: "level_up",
      category: "progress_level",
      mode: Number(payload.newLevel || 0) > 10 ? "modal" : "toast",
      level: payload.newLevel,
      title: `Новый уровень: ${payload.newLevel}`,
      description: `Достигнут уровень ${payload.newLevel}`,
      metadata: {
        newLevel: payload.newLevel,
        experienceGained: Number(payload.experienceGained || experience) || 0
      },
      key: `level:${payload.newLevel}`
    });
    return ok;
  }, [logActivity, notify]);

  const logFavorite = useCallback((data: ActivityLogMetadata | string | undefined) => {
    const payload = typeof data === "object" && data ? data : { filmTitle: data };
    const filmTitle = payload.filmTitle || payload.title || payload.name || "Фильм";
    return logActivity(
      "favorite",
      "Добавлено в избранное",
      `«${filmTitle}»`,
      0,
      {
        ...payload,
        filmTitle,
        dedupeKey: `favorite:${payload.filmId || filmTitle}`
      }
    );
  }, [logActivity]);

  const logWatchlist = useCallback((data: ActivityLogMetadata | string | undefined) => {
    const payload = typeof data === "object" && data ? data : { filmTitle: data };
    const filmTitle = payload.filmTitle || payload.title || payload.name || "Фильм";
    return logActivity(
      "watchlist",
      "Добавлено в «Буду смотреть»",
      `«${filmTitle}»`,
      0,
      {
        ...payload,
        filmTitle,
        dedupeKey: `watchlist:${payload.filmId || filmTitle}`
      }
    );
  }, [logActivity]);

  const logFriendAdded = useCallback((data: ActivityLogMetadata | string | undefined) => {
    const payload = typeof data === "object" && data ? data : { friendName: data };
    const friendName = payload.friendName || payload.name || payload.displayName || "Пользователь";
    return logActivity(
      "friend_added",
      "Новый друг",
      `${friendName} добавлен в друзья`,
      0,
      {
        ...payload,
        friendName,
        dedupeKey: `friend:${payload.friendId || payload.friendUid || friendName}`
      }
    );
  }, [logActivity]);

  const logChallengeCompleted = useCallback(async (challenge: ProgressExperienceItem) => {
    const experience = Number(challenge.experience) || 0;
    const ok = await logActivity(
      "challenge",
      `Выполнено испытание «${challenge.title}»`,
      challenge.description || "Испытание завершено",
      experience,
      {
        challengeId: challenge.id,
        challengeTitle: challenge.title,
        challengeDifficulty: challenge.difficulty,
        dedupeKey: `challenge:${challenge.id}`
      }
    );
    if (ok) notify({
      scope: "own",
      type: "challenge",
      category: "progress_challenge",
      mode: "modal",
      title: `Испытание выполнено: ${challenge.title}`,
      description: experience ? `+${experience} опыта` : challenge.description || "Испытание завершено",
      experience,
      metadata: { challengeId: challenge.id, challengeTitle: challenge.title },
      key: `challenge:${challenge.id}`
    });
    return ok;
  }, [logActivity, notify]);

  const logReview = useCallback((data: ActivityLogMetadata | string | undefined) => {
    const payload = typeof data === "object" ? data : { filmTitle: data };
    return logActivity(
      "review",
      `Написана рецензия «${payload.filmTitle || "Фильм"}»`,
      payload.reviewTitle ? `«${payload.reviewTitle}»` : "Новая рецензия",
      0,
      { ...payload, dedupeKey: `review:${payload.filmId || payload.filmTitle || payload.reviewTitle}` }
    );
  }, [logActivity]);

  return {
    logActivity,
    hasActivity,
    removeActivity,
    logRating,
    logAchievement,
    logExperienceAdjustment,
    logStreakProgress,
    logLevelUp,
    logFavorite,
    logWatchlist,
    logFriendAdded,
    logChallengeCompleted,
    logReview
  };
};
